import { graphql, useStaticQuery, Link } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import React, { useEffect, useRef, useState } from "react";
import IconInstagram from "../../assets/IconInstagram";
import IconLinkedIn from "../../assets/IconLinkedIn";
import Button from '../button';
import IconEmployeeOwned from "../../assets/IconEmployeeOwned";
import IconBCorp from "../../assets/IconBCorp";
import iconIso27001 from "../../assets/Icon27001.png";
import Section from "../section/Section";
import Modal from "../modal/Modal";
import Mailchimp from "../mailchimp/Mailchimp";
import * as styles from "./Footer.module.css";

const Footer = ({ location }) => {
  const { contentfulFooter } = useStaticQuery(graphql`
    query {
      contentfulFooter {
        twitter
        linkedIn
        instagram
      }
    }
  `);

  const {
    twitter,
    linkedIn,
    instagram,
  } = contentfulFooter;

  const [fontSize, setFontSize] = useState('4vw');
  const helloWordsRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Handle newsletter button click - add query parameter and open modal
  const handleNewsletterClick = () => {
    if (location && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(location.search);
      // Add newsletter parameter
      searchParams.set('newsletter', 'true');
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}?${newSearch}${location.hash || ''}`;
      
      // Update URL with query parameter
      window.history.pushState({}, '', newUrl);
    }
    setIsModalOpen(true);
  };
  
  // Handle modal close - remove query parameter from URL
  const handleModalClose = () => {
    setIsModalOpen(false);
    
    if (location && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has('newsletter')) {
        searchParams.delete('newsletter');
        const newSearch = searchParams.toString();
        const newUrl = newSearch 
          ? `${location.pathname}?${newSearch}${location.hash || ''}`
          : `${location.pathname}${location.hash || ''}`;
        
        // Update URL without page reload
        window.history.replaceState({}, '', newUrl);
      }
    }
  };
  
  // Check for newsletter parameter in URL and open modal if present (on initial load)
  useEffect(() => {
    if (location && typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(location.search);
      const newsletterParam = searchParams.get('newsletter');
      
      if (newsletterParam === 'true' || newsletterParam === '') {
        setIsModalOpen(true);
      }
    }
  }, [location]);
  
  useEffect(() => {
    const calculateFontSize = () => {
      if (helloWordsRef.current) {
        const viewportWidth = window.innerWidth;
        
        // Count characters in each row (including spaces)
        const topRow = "LISBON OLÁ HALLO HI LONDON HEJ MALMÖ"; // 7 words, 6 spaces
        const bottomRow = "NEW YORK HELLO AHOJ TOKYO やあ HOLA"; // 6 words, 5 spaces
        
        // Count actual characters (including spaces)
        const topRowChars = topRow.length; // 35 characters
        const bottomRowChars = bottomRow.length; // 32 characters
        
        // Use the longer row for calculation
        const maxChars = Math.max(topRowChars, bottomRowChars);
        
        // For monospace fonts: 100vw / characterCount = font size needed
        // For proportional fonts: we need to account for average character width
        // Assuming average character width is about 0.6 of font size
        const averageCharWidth = 0.6;
        const targetFontSize = (100 / (maxChars * averageCharWidth));
        
        // Set bounds to prevent extreme sizes
        const clampedFontSize = Math.max(1.5, Math.min(6, targetFontSize));
        
        setFontSize(`${clampedFontSize + 5}vw`);
      }
    };

    // Calculate on mount and resize
    calculateFontSize();
    window.addEventListener('resize', calculateFontSize);
    
    return () => window.removeEventListener('resize', calculateFontSize);
  }, []);
  
  return (
    <>
      <footer className={styles.footer}>
        <Section>
          <div className={styles.footerGrid}>
            <div className={styles.leftColumn}>
              <div>
                <ul className={styles.listLinks}>
                  <li><Link to="/what-we-do/" className="smallBodyText">What we do</Link></li>
                  <li><Link to="/work/" className="smallBodyText">Work</Link></li>
                  <li><Link to="/ai/" className="smallBodyText">AI</Link></li>
                  <li><Link to="/blog/" className="smallBodyText">Insights</Link></li>
                  <li><Link to="/our-story/" className="smallBodyText">Our story</Link></li>
                  <li><Link to="/employee-owned/" className="smallBodyText">Employee owned</Link></li>
                  <li><Link to="/b-corp/" className="smallBodyText">B Corp</Link></li>
                  <li><Link to="/join-us/" className="smallBodyText">Join us</Link></li>
                  <li><Link to="/contact-us/" className="smallBodyText">Contact us</Link></li>
                </ul>
              </div>
            </div>
            <div className={styles.middleColumn}>
            <div className={styles.socialNetworks}>
                <p className="smallestBodyText noMargin">Follow us</p>
                <ul className={styles.socialNetworks}>
                  <li>
                    <a
                      href={instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconInstagram />
                    </a>
                  </li>
                  <li>
                    <a
                      href={linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconLinkedIn />
                    </a>
                  </li>
                </ul>
              </div>
              <p className="smallestBodyText" style={{ marginTop: '10px' }}>Sign up to our <button onClick={handleNewsletterClick} aria-label="Open modal">newsletter</button></p>
              <p className={`smallestBodyText ${styles.green}`}>This website is hosted on green servers located in regions with renewable energy generation. <Link to="/blog/reducing-the-carbon-footprint-of-our-website/">Read what we did</Link></p>
              
            </div>
            <div className={styles.rightColumn}>
              <div className={styles.logos}>
                <IconEmployeeOwned fill="#ffffff" width="130" />
                <IconBCorp fill="#ffffff" width="70" style={{ transform: "translateY(-8px)" }} />
                <img src={iconIso27001} alt="ISO 20071 Information Security Management" style={{ width: "100px" }} />
              </div>
              <p className={`${styles.smallPrint} smallestBodyText textAlignRight`}><Link to="/legal/">Legal</Link> | <Link to="/privacy-policy/">Privacy Policy</Link> | <Link to="/accessibility-help/">Accessibility Help</Link><br />Copyright © ustwo Ltd {new Date().getFullYear()}. All rights reserved. </p>
            </div>
          </div>
        </Section>
        <Section className={styles.footerBottom} noInner>
          <div className={styles.helloFooter}>
            <div className={styles.helloWords} ref={helloWordsRef} style={{ fontSize }}>
              <div className={styles.helloRow}>
                <span className={`${styles.helloWord} brandFont`}>LISBON</span>
                <span className={`${styles.helloWord} brandFont`}>OLÁ</span>
                <span className={`${styles.helloWord} brandFont`}>HALLO</span>
                <span className={`${styles.helloWord} brandFont`}>HI</span>
                <span className={`${styles.helloWord} brandFont`}>LONDON</span>
                <span className={`${styles.helloWord} brandFont`}>HEJ</span>
                <span className={`${styles.helloWord} brandFont`}>MALMÖ</span>
              </div>
              <div className={styles.helloRow}>
                <span className={`${styles.helloWord} brandFont`}>NEW YORK</span>
                <span className={`${styles.helloWord} brandFont`}>HELLO</span>
                <span className={`${styles.helloWord} brandFont`}>AHOJ</span>
                <span className={`${styles.helloWord} brandFont`}>TOKYO</span>
                <span className={`${styles.helloWord} brandFont`}>やあ</span>
                <span className={`${styles.helloWord} brandFont`}>HOLA</span>
              </div>
            </div>
            <div className={styles.contactButton}>
              <Button variant="round" to="/contact-us/" ariaLabel="Contact us" symbol="@" invert>Contact us</Button>
            </div>
          </div>
        </Section>
        
      </footer>
      <Modal
        isOpen={isModalOpen} 
        onClose={handleModalClose}
        padding="both"
        backgroundColor="white"
      >
        <div className={styles.modalGrid}>
          <div className={styles.modalColumn}>
            <Mailchimp onClose={handleModalClose} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Footer;
